import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container } from '@mui/material'
import Header from '../dashboard/header'
import Card from '@/components/ui/card'
import Button from '@/components/ui/button'
import Badge from '@/components/badge'
import { mockDatasources } from '@/mocks/datasources/mockData'
import { DatasourceType, ConnectionStatus } from '@/services/datasources/types'
import {
  DetailWrapper,
  BackButton,
  StatusBadge,
  InfoSection,
  InfoGrid,
  InfoItem,
  ConnectionInfo,
  ConfigSection,
  TagsSection,
  Tag,
  ActionsSection,
  StatsCard,
  StatValue,
  StatLabel,
} from './styled'

const DatasourceDetail: React.FC = () => {
  const { datasourceId } = useParams<{ datasourceId: string }>()
  const navigate = useNavigate()

  // In a real app, this would be fetched via API
  const datasource = mockDatasources.find((ds) => ds.id === datasourceId)

  if (!datasource) {
    return (
      <Container maxWidth="xl" sx={{ padding: '30px' }}>
        <h2>Datasource not found</h2>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </Container>
    )
  }

  const getStatusColor = (status: ConnectionStatus) => {
    switch (status) {
      case ConnectionStatus.CONNECTED:
        return '#4BA765'
      case ConnectionStatus.ERROR:
        return '#FF4444'
      case ConnectionStatus.PENDING:
        return '#FFA500'
      case ConnectionStatus.DISCONNECTED:
        return '#666666'
      default:
        return '#666666'
    }
  }

  const getTypeLabel = (type: DatasourceType) => {
    switch (type) {
      case DatasourceType.PRODUCT_CATALOG:
        return 'Marketplace Product'
      case DatasourceType.SNOWFLAKE:
        return 'Snowflake'
      case DatasourceType.BLOOMBERG:
        return 'Bloomberg Terminal'
      case DatasourceType.REUTERS:
        return 'Reuters/Refinitiv'
      case DatasourceType.DATABASE:
        return 'Database'
      case DatasourceType.API:
        return 'REST API'
      case DatasourceType.STREAMING:
        return 'Streaming'
      case DatasourceType.FILE_SYSTEM:
        return 'File Storage'
      case DatasourceType.DATADOG:
        return 'Datadog'
      default:
        return type
    }
  }

  const formatNumber = (num?: number) => {
    if (!num) return '0'
    return new Intl.NumberFormat('en-US').format(num)
  }

  return (
    <Container maxWidth="xl" sx={{ padding: '30px' }}>
      <BackButton onClick={() => navigate(-1)}>← Back</BackButton>

      <Header title={datasource.name} description={datasource.description}>
        <ConnectionInfo>
          <StatusBadge color={getStatusColor(datasource.connectionStatus)}>
            {datasource.connectionStatus}
          </StatusBadge>
          <Badge text={getTypeLabel(datasource.type)} variant="primary" />
        </ConnectionInfo>
      </Header>

      <DetailWrapper>
        {/* Connection Info */}
        <InfoSection>
          <Card>
            <h3>Connection Information</h3>
            <InfoGrid>
              <InfoItem>
                <span className="label">Type</span>
                <span className="value">{getTypeLabel(datasource.type)}</span>
              </InfoItem>
              {datasource.provider && (
                <InfoItem>
                  <span className="label">Provider</span>
                  <span className="value">{datasource.provider}</span>
                </InfoItem>
              )}
              <InfoItem>
                <span className="label">Created</span>
                <span className="value">
                  {new Date(datasource.createdAt).toLocaleDateString()}
                </span>
              </InfoItem>
              <InfoItem>
                <span className="label">Last Updated</span>
                <span className="value">
                  {new Date(datasource.updatedAt).toLocaleDateString()}
                </span>
              </InfoItem>
              {datasource.lastConnected && (
                <InfoItem>
                  <span className="label">Last Connected</span>
                  <span className="value">
                    {new Date(datasource.lastConnected).toLocaleString()}
                  </span>
                </InfoItem>
              )}
            </InfoGrid>
          </Card>
        </InfoSection>

        {/* Metadata */}
        {datasource.metadata && (
          <InfoSection>
            <Card>
              <h3>Data Characteristics</h3>
              <InfoGrid>
                {Object.entries(datasource.metadata).map(([key, value]) => (
                  <InfoItem key={key}>
                    <span className="label">
                      {key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, (str) => str.toUpperCase())}
                    </span>
                    <span className="value">{value}</span>
                  </InfoItem>
                ))}
              </InfoGrid>
            </Card>
          </InfoSection>
        )}

        {/* Configuration (masked) */}
        {datasource.config && (
          <ConfigSection>
            <Card>
              <h3>Configuration</h3>
              <InfoGrid>
                {Object.entries(datasource.config).map(([key, value]) => (
                  <InfoItem key={key}>
                    <span className="label">{key}</span>
                    <span className="value">
                      {key.toLowerCase().includes('key') ||
                      key.toLowerCase().includes('password')
                        ? '••••••••••'
                        : String(value)}
                    </span>
                  </InfoItem>
                ))}
              </InfoGrid>
            </Card>
          </ConfigSection>
        )}

        {/* Usage Statistics */}
        {datasource.usageStats && (
          <InfoSection>
            <h3>Usage Statistics</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}
            >
              <StatsCard>
                <StatValue>
                  {formatNumber(datasource.usageStats.totalQueries)}
                </StatValue>
                <StatLabel>Total Queries</StatLabel>
              </StatsCard>
              {datasource.usageStats.avgResponseTime !== undefined && (
                <StatsCard>
                  <StatValue>
                    {datasource.usageStats.avgResponseTime}ms
                  </StatValue>
                  <StatLabel>Avg Response Time</StatLabel>
                </StatsCard>
              )}
              {datasource.usageStats.errorRate !== undefined && (
                <StatsCard>
                  <StatValue>
                    {(datasource.usageStats.errorRate * 100).toFixed(2)}%
                  </StatValue>
                  <StatLabel>Error Rate</StatLabel>
                </StatsCard>
              )}
              {datasource.usageStats.lastQueried && (
                <StatsCard>
                  <StatValue style={{ fontSize: '16px' }}>
                    {new Date(
                      datasource.usageStats.lastQueried,
                    ).toLocaleString()}
                  </StatValue>
                  <StatLabel>Last Queried</StatLabel>
                </StatsCard>
              )}
            </div>
          </InfoSection>
        )}

        {/* Tags */}
        {datasource.tags && datasource.tags.length > 0 && (
          <TagsSection>
            <h3>Tags</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {datasource.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </div>
          </TagsSection>
        )}

        {/* Actions */}
        <ActionsSection>
          {datasource.productId && (
            <Button
              onClick={() => navigate(`/product/${datasource.productId}`)}
              variant="primary"
            >
              View in Marketplace
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              /* Test connection logic */
            }}
          >
            Test Connection
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              /* Configure logic */
            }}
          >
            Configure
          </Button>
        </ActionsSection>
      </DetailWrapper>
    </Container>
  )
}

export default DatasourceDetail
